import React, { useCallback, useRef, useEffect, useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  AccessTime as AccessTimeIcon,
  EventRepeat as EventRepeatIcon,
} from '@mui/icons-material';
import { OpeningHoursEntry } from '../../types/Location';
import { v4 as uuidv4 } from 'uuid';
import { DayValue, getAllDayValues } from '../../utils/dayUtils';
import { useTranslation } from 'react-i18next';
import OpeningHoursRecurrenceDialog from './OpeningHoursRecurrenceDialog';
import { formatRecurrenceSummary, hasOpeningTimeExtras } from '../../utils/openingTimeUtils';
import { hasLocalizedText } from '../../utils/localizedText';

interface OpeningHoursTableProps {
  value: OpeningHoursEntry[];
  onChange: (value: OpeningHoursEntry[]) => void;
  label: string;
  icon?: React.ReactNode;
  languageCodes?: string[];
}

export interface OpeningHoursTableRef {
  getCurrentValue: () => OpeningHoursEntry[];
}

const weekdays = getAllDayValues();

// Custom Time Input Component
const CustomTimeInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}> = ({ value, onChange, onBlur, label, size = 'small', fullWidth = true }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(value);

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleNativeTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
  };

  const handleBlur = () => {
    // Only update parent if value changed
    if (localValue !== value) {
      onChange(localValue);
    }
    onBlur?.();
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        type="time"
        value={localValue}
        onChange={handleNativeTimeChange}
        onBlur={handleBlur}
        label={label}
        size={size}
        fullWidth={fullWidth}
        InputLabelProps={{ shrink: true }}
        inputProps={{
          step: 300, // 5 minutes
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <AccessTimeIcon />
            </InputAdornment>
          ),
        }}
        ref={inputRef}
      />
    </Box>
  );
};

const OpeningHoursTable = forwardRef<OpeningHoursTableRef, OpeningHoursTableProps>(({
  value,
  onChange,
  label,
  icon,
  languageCodes = ['de', 'en'],
}, ref) => {
  const { t } = useTranslation();
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const lastFocusedRef = useRef<string>('');
  const [recurrenceDialogEntryId, setRecurrenceDialogEntryId] = useState<string | null>(null);
  
  // Local state for changes - only update parent when explicitly requested
  const [localValue, setLocalValue] = useState<OpeningHoursEntry[]>(() => 
    value.map((entry, index) => ({
      ...entry,
      id: entry.id || `temp-${uuidv4()}`
    }))
  );

  // Expose getCurrentValue to parent via ref
  useImperativeHandle(ref, () => ({
    getCurrentValue: () => localValue
  }), [localValue]);

  const addRow = useCallback(() => {
    const newEntry: OpeningHoursEntry = {
      id: uuidv4(),
      day: '0', // Default to Monday
      from: '09:00',
      to: '17:00'
    };
    const updated = [...localValue, newEntry];
    setLocalValue(updated);
    // Don't call onChange - keep it local
  }, [localValue]);

  const updateRow = useCallback((id: string, field: 'day' | 'from' | 'to', newValue: string) => {
    const currentEntry = localValue.find((entry) => entry.id === id);
    if (currentEntry && currentEntry[field] === newValue) {
      return;
    }

    lastFocusedRef.current = `${id}-${field}`;

    const updated = localValue.map((entry) =>
      entry.id === id ? { ...entry, [field]: newValue } : entry
    );
    setLocalValue(updated);
  }, [localValue]);

  const updateEntryExtras = useCallback((
    id: string,
    updates: Pick<OpeningHoursEntry, 'recurrence' | 'note'>
  ) => {
    const updated = localValue.map((entry) =>
      entry.id === id ? { ...entry, ...updates } : entry
    );
    setLocalValue(updated);
  }, [localValue]);

  const recurrenceDialogEntry = localValue.find((entry) => entry.id === recurrenceDialogEntryId) ?? null;

  const handleDeleteRow = useCallback((id: string) => {
    console.log('Deleting row with id:', id);
    console.log('Before deletion, localValue:', localValue);
    const updated = localValue.filter(entry => entry.id !== id);
    console.log('After deletion, updated:', updated);
    setLocalValue(updated);
    // Don't call onDelete - keep it local
  }, [localValue]);

  // Restore focus after re-render
  useEffect(() => {
    if (lastFocusedRef.current) {
      const input = inputRefs.current[lastFocusedRef.current];
      if (input) {
        input.focus();
        // Move cursor to end
        const length = input.value.length;
        input.setSelectionRange(length, length);
      }
    }
  });

  const sortedEntries = useMemo(() => {
    return [...localValue].sort((a, b) => {
      const aIndex = parseInt(a.day);
      const bIndex = parseInt(b.day);
      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }
      // If same day, sort by from time
      return a.from.localeCompare(b.from);
    });
  }, [localValue]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && (
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.primary', fontSize: '1.25rem' }}>
              {icon}
            </Box>
          )}
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>{label}</h4>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRow}
          size="small"
        >
          {t('openingHoursTable.addRow')}
        </Button>
      </Box>
      
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="22%">{t('openingHoursTable.weekday')}</TableCell>
              <TableCell width="18%">{t('openingHoursTable.from')}</TableCell>
              <TableCell width="18%">{t('openingHoursTable.to')}</TableCell>
              <TableCell width="28%">{t('openingHoursTable.recurrenceColumn')}</TableCell>
              <TableCell width="14%" align="center">{t('openingHoursTable.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedEntries.map((entry) => {
              const recurrenceSummary = formatRecurrenceSummary(
                entry.recurrence,
                entry.day as DayValue,
                t
              );

              return (
                <TableRow key={entry.id}>
                  <TableCell width="22%">
                    <Select
                      value={entry.day}
                      onChange={(e) => updateRow(entry.id!, 'day', e.target.value)}
                      size="small"
                      fullWidth
                    >
                      {weekdays.map(day => (
                        <MenuItem key={day} value={day}>
                          {t(`openingHours.${day}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell width="18%">
                    <CustomTimeInput
                      value={entry.from}
                      onChange={(newValue) => updateRow(entry.id!, 'from', newValue)}
                    />
                  </TableCell>
                  <TableCell width="18%">
                    <CustomTimeInput
                      value={entry.to}
                      onChange={(newValue) => updateRow(entry.id!, 'to', newValue)}
                    />
                  </TableCell>
                  <TableCell width="28%">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {recurrenceSummary ? (
                          <Tooltip title={recurrenceSummary}>
                            <Chip
                              size="small"
                              label={recurrenceSummary}
                              color="primary"
                              variant="outlined"
                              sx={{ maxWidth: '100%' }}
                            />
                          </Tooltip>
                        ) : hasLocalizedText(entry.note) ? (
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {t('openingHoursTable.recurrence.noteOnly')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('openingHoursTable.recurrence.defaultWeekly')}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title={t('openingHoursTable.recurrence.edit')}>
                        <IconButton
                          onClick={() => setRecurrenceDialogEntryId(entry.id!)}
                          size="small"
                          color={hasOpeningTimeExtras(entry) ? 'primary' : 'default'}
                        >
                          <EventRepeatIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell width="14%" align="center">
                    <IconButton
                      onClick={() => handleDeleteRow(entry.id!)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedEntries.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  {t('openingHoursTable.noOpeningHours')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <OpeningHoursRecurrenceDialog
        open={Boolean(recurrenceDialogEntry)}
        entry={recurrenceDialogEntry}
        day={(recurrenceDialogEntry?.day ?? '0') as DayValue}
        languageCodes={languageCodes}
        onClose={() => setRecurrenceDialogEntryId(null)}
        onSave={(updates) => {
          if (recurrenceDialogEntryId) {
            updateEntryExtras(recurrenceDialogEntryId, updates);
          }
        }}
      />
    </Box>
  );
});

OpeningHoursTable.displayName = 'OpeningHoursTable';

export default OpeningHoursTable; 